import { useEffect, useRef, useState } from "react";
import Reveal from "@/components/Reveal";
import { Eye, Shield, MessageCircle, ShoppingCart, Play, Zap, Check, User, ArrowRight } from "lucide-react";

const TEAL = "#00A0A0";
const YELLOW = "#FFD700";
const RED = "#ff4d4d";

const COLOR = { t: "text-[#2dd4c3]", r: "text-[#ff4d4d]", y: "text-[#FFD700]", "": "" };

const REELS = [
  { t: "ENTREGA", h: [["¿Por qué ", ""], ["nos eligió?", "t"]] },
  { t: "CONSEJO", h: [["El error que más ", ""], ["dinero", "r"], [" cuesta", ""]] },
  { t: "HISTORIA", h: [["3 meses buscando ", ""], ["este coche", "y"]] },
  { t: "VLOG", h: [["Lo que ", ""], ["nadie", "t"], [" te cuenta", ""]] },
  { t: "EDUCACIÓN", h: [["El 90% lo hace ", ""], ["mal", "r"]] },
  { t: "AUTORIDAD", h: [["Charla con un ", ""], ["experto", "t"]] },
  { t: "RESEÑA", h: [["Lo que dicen ", ""], ["mis clientes", "t"]] },
  { t: "PROCESO", h: [["Cómo ", ""], ["peritamos", "y"], [" cada coche", ""]] },
  { t: "CONFIANZA", h: [["No vendo ", ""], ["cualquier", "r"], [" coche", ""]] },
];

const PRICES = ["13.900", "9.450", "21.990", "7.800", "16.500", "11.250", "18.900", "6.990", "24.500"];
const STICKERS = ["OFERTA", "REBAJADO", "ÚLTIMO", "-15%", "NUEVO", "GANGA", "LIQUIDA", "OCASIÓN", "¡YA!"];

const CarGhost = ({ className, opacity = 0.5 }) => (
  <svg className={className} style={{ opacity }} viewBox="0 0 200 90" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 64 L14 52 Q16 44 40 42 Q86 36 112 33 Q120 24 138 20 L150 20 Q166 22 176 33 Q186 38 188 50 L186 64 L150 64 A16 16 0 0 0 118 64 L70 64 A16 16 0 0 0 38 64 Z" fill="#5fd8c6" />
    <circle cx="54" cy="64" r="13" fill="#cdd5d3" />
    <circle cx="134" cy="64" r="13" fill="#cdd5d3" />
  </svg>
);

const Hook = ({ parts, className }) => (
  <span className={className}>
    {parts.map(([txt, c], i) => (
      <span key={i} className={COLOR[c]}>{txt}</span>
    ))}
  </span>
);

const FLOW = [
  { icon: Eye, nm: "Atención", de: "Te descubren con contenido que engancha.", accent: RED },
  { icon: Shield, nm: "Confianza", de: "Te conocen, te entienden y confían.", accent: TEAL },
  { icon: MessageCircle, nm: "Conversación", de: "Te escriben con intención real.", accent: TEAL },
  { icon: ShoppingCart, nm: "Venta", de: "Cierras más negocios y mejores clientes.", accent: TEAL },
];

export default function Comparativa() {
  const [idx, setIdx] = useState(0);
  const [runId, setRunId] = useState(0);
  const [views, setViews] = useState(0);
  const [comments, setComments] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [showSale, setShowSale] = useState(false);
  const [step, setStep] = useState(1);
  const timersRef = useRef([]);
  const viewTimerRef = useRef(null);
  const threadRef = useRef(null);

  useEffect(() => {
    const at = (ms, fn) => timersRef.current.push(setTimeout(fn, ms));
    const addMsg = (m) => setMsgs((p) => [...p.filter((x) => x.type !== "typing"), m]);

    setComments([]); setMsgs([]); setShowSale(false); setStep(1); setViews(0);

    let v = 0;
    viewTimerRef.current = setInterval(() => {
      v += Math.floor(180 + Math.random() * 240);
      setViews(v);
    }, 120);

    at(2300, () => { setStep(2); setComments((p) => [...p, { who: "marcos_g", txt: "Brutal el coche, qué limpio todo" }]); });
    at(3000, () => setComments((p) => [...p, { who: "rocio.m", txt: "¿Sigue disponible?" }]));
    at(3700, () => setComments((p) => [...p, { who: "javi_drives", txt: "Justo lo que estoy buscando" }]));
    at(4900, () => {
      clearInterval(viewTimerRef.current);
      setStep(3);
      addMsg({ type: "them", txt: "Hola! Vi el vídeo. ¿Sigue disponible el coche?" });
    });
    at(5800, () => addMsg({ type: "typing" }));
    at(6600, () => addMsg({ type: "me", auto: true, txt: "¡Hola Carlos! Sí, sigue disponible. ¿Lo quieres para ti o para empresa?" }));
    at(7600, () => addMsg({ type: "them", txt: "Para mí. ¿Podría verlo esta semana?" }));
    at(8500, () => addMsg({ type: "typing" }));
    at(9300, () => addMsg({ type: "me", auto: true, txt: "Claro. Te reservo el jueves a las 18:00 y te lo confirmo por aquí." }));
    at(10100, () => addMsg({ type: "sys", txt: "Lead cualificado → enviado al equipo" }));
    at(11100, () => { setStep(4); setShowSale(true); });
    at(13900, () => { setIdx((i) => (i + 1) % REELS.length); setRunId((r) => r + 1); });

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      clearInterval(viewTimerRef.current);
    };
  }, [runId]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [msgs]);

  const jumpTo = (i) => { setIdx(i); setRunId((r) => r + 1); };
  const reel = REELS[idx];

  return (
    <section id="comparativa" data-testid="comparativa-section" className="relative py-24 lg:py-32 border-t border-white/5 overflow-hidden">
      <style>{`
        @keyframes cmp-fill { from { width: 0 } to { width: 100% } }
        @keyframes cmp-pulse { 0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0,160,160,.5) } 50% { transform: scale(1.06); box-shadow: 0 0 0 14px rgba(0,160,160,0) } }
        @keyframes cmp-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
        @keyframes cmp-pop { from { transform: scale(.4); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        @keyframes cmp-blink { 0%,60%,100% { opacity: .25 } 30% { opacity: 1 } }
        .cmp-anim-in { animation: cmp-in .35s ease forwards }
      `}</style>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(90% 70% at 80% 0%, rgba(0,160,160,.08), transparent 55%)" }} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">

        {/* Cabecera */}
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
            <div className="font-stencil text-xs text-[#00A0A0] tracking-widest mb-5">02 / LA DIFERENCIA</div>
            <h2 className="font-heading text-white text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-[1.02]">
              La mayoría publica coches.
              <br />
              <span className="text-[#00A0A0]">Los mejores construyen confianza.</span>
            </h2>
            <p className="text-white/55 font-body mt-4 text-base lg:text-lg">
              El mismo coche, dos formas de venderlo. Una compite por precio. La otra hace que te elijan a ti.
            </p>
          </div>
        </Reveal>

        {/* Comparativa parrillas */}
        <div className="grid lg:grid-cols-[1fr_64px_1fr] gap-4 items-start max-w-5xl mx-auto">
          {/* Teletienda */}
          <Reveal>
            <div className="flex items-center gap-2.5 flex-wrap mb-3">
              <span className="font-stencil text-[11px] tracking-widest uppercase px-3 py-1.5 rounded bg-[#ff4d4d] text-[#2a0000]">Lo de siempre</span>
              <span className="font-stencil text-[11px] tracking-widest uppercase text-white/40">Perfil teletienda</span>
            </div>
            <div data-testid="grid-bad" className="bg-[#0c100f] border border-[#222b2a] rounded-2xl p-3.5">
              <div className="flex items-center gap-2.5 pb-3 px-0.5">
                <div className="w-9 h-9 rounded-full border border-[#2a3432] flex items-center justify-center flex-none">
                  <CarGhost className="w-5" opacity={0.55} />
                </div>
                <div>
                  <div className="text-[13.5px] font-bold text-white leading-tight">@tu_compraventa</div>
                  <div className="text-[11px] text-[#69716f]">412 publicaciones</div>
                </div>
                <span className="ml-auto text-[#5a615f] font-black tracking-wider">···</span>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {STICKERS.map((s, i) => (
                  <div key={i} className="relative aspect-[3/4] rounded-md overflow-hidden bg-[#15201d] flex items-center justify-center">
                    <CarGhost className="w-[84%]" />
                    <span className="absolute top-1 left-1 bg-[#ff4d4d] text-white font-black text-[8.5px] px-1.5 py-0.5 rounded tracking-wide">{s}</span>
                    <span className="absolute bottom-1.5 left-1.5 bg-[#FFD700] text-[#1a1500] font-black text-[11px] px-1.5 py-0.5 rounded">{PRICES[i]}€</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-4 font-bold text-[15px] text-[#ff8c8c]">
              Compiten por <span className="text-[#ff4d4d]">precio</span>.
              <span className="block text-white/50 font-medium text-[13px] mt-1 font-body">Clientes que comparan, dudan y no confían.</span>
            </p>
          </Reveal>

          {/* VS */}
          <div className="hidden lg:flex self-center justify-self-center w-14 h-14 rounded-full bg-[#0c100f] border-2 border-[#00A0A0] items-center justify-center font-stencil text-base text-white" style={{ boxShadow: "0 0 22px -4px rgba(0,160,160,.7)" }}>
            VS
          </div>

          {/* Fuelkers */}
          <Reveal delay={120}>
            <div className="flex items-center gap-2.5 flex-wrap mb-3">
              <span className="font-stencil text-[11px] tracking-widest uppercase px-3 py-1.5 rounded bg-[#00A0A0] text-[#06201c]">Sistema Fuelkers</span>
              <span className="font-stencil text-[11px] tracking-widest uppercase text-white/40">Perfil que genera confianza</span>
            </div>
            <div data-testid="grid-good" className="bg-[#0c100f] border border-[#1f3a35] rounded-2xl p-3.5" style={{ boxShadow: "0 0 32px -16px rgba(0,160,160,.6)" }}>
              <div className="flex items-center gap-2.5 pb-3 px-0.5">
                <div className="w-9 h-9 rounded-full border border-[#00A0A0] flex items-center justify-center flex-none">
                  <Zap className="w-4 h-4 text-[#00A0A0]" />
                </div>
                <div>
                  <div className="text-[13.5px] font-bold text-white leading-tight">@tu_marca</div>
                  <div className="text-[11px] text-[#69716f]">Contenido que vende por ti</div>
                </div>
                <span className="ml-auto text-[#5a615f] font-black tracking-wider">···</span>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {REELS.map((r, i) => (
                  <button
                    key={i}
                    data-testid={`reel-cell-${i}`}
                    onMouseEnter={() => jumpTo(i)}
                    onClick={() => jumpTo(i)}
                    className={`relative aspect-[3/4] rounded-md overflow-hidden flex flex-col items-start justify-start p-2 text-left border transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:scale-[1.02] ${
                      i === idx ? "border-[#00A0A0] ring-2 ring-[#00A0A0]" : "border-[#1d342f]"
                    }`}
                    style={{
                      background: "radial-gradient(120% 90% at 70% 0%, #173a34, #0a1413)",
                      boxShadow: i === idx ? "0 0 20px -2px rgba(0,160,160,.75)" : "none",
                    }}
                  >
                    <span className="bg-[#ff4d4d] text-white font-black text-[7.5px] tracking-wide px-1 py-0.5 rounded">{r.t}</span>
                    <Hook parts={r.h} className="mt-auto font-stencil text-[12px] lg:text-[13px] leading-[0.98] uppercase text-white" />
                    <span className={`absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full bg-[#00A0A0]/85 flex items-center justify-center transition-transform duration-300 ${i === idx ? "scale-125" : ""}`}>
                      <Play className="w-2.5 h-2.5 text-[#06201c]" fill="currentColor" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-4 font-bold text-[15px] text-[#00A0A0]">
              Construyen confianza.
              <span className="block text-white/50 font-medium text-[13px] mt-1 font-body">Clientes que te conocen, confían y te eligen.</span>
            </p>
          </Reveal>
        </div>

        {/* Demo animado */}
        <div className="max-w-5xl mx-auto mt-14 lg:mt-20">
          <Reveal>
            <h3 className="text-center font-stencil text-white text-xl lg:text-2xl uppercase tracking-wide">Así trabaja el sistema</h3>
            <p className="text-center text-white/50 font-body text-sm mt-1">Una publicación que se reproduce sola… hasta cerrar la venta.</p>
            <div className="flex items-center justify-center gap-2 text-[#00A0A0] font-bold text-[12.5px] mt-4 mb-4">
              <ArrowRight className="w-4 h-4 rotate-[135deg]" />
              Pasa el ratón por un reel de <b className="text-white">@tu_marca</b> y míralo trabajar aquí
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div data-testid="demo-stage" className="relative rounded-2xl border border-[#1f3a35] overflow-hidden p-4 lg:p-5" style={{ background: "linear-gradient(160deg, #0c1817, #08110f)" }}>
              <div className="flex flex-col md:flex-row gap-4 lg:gap-5 items-stretch">

                {/* Reel */}
                <div className="flex-1 min-w-0 flex flex-col bg-white/[0.02] border border-[#1c2c2a] rounded-xl p-3">
                  <div className="relative h-[174px] rounded-lg flex-none border border-[#235049] overflow-hidden flex items-center justify-center" style={{ background: "radial-gradient(120% 100% at 60% 20%, #16352f, #0a1413)" }}>
                    <CarGhost className="w-[74%]" opacity={0.16} />
                    <span data-testid="reel-tag" className="absolute top-2 left-2 bg-[#ff4d4d] text-white font-black text-[9px] tracking-wide px-1.5 py-0.5 rounded">{reel.t}</span>
                    <span data-testid="reel-views" className="absolute top-2 right-2 bg-black/45 text-[#dfe6e4] font-bold text-[11px] px-2 py-0.5 rounded-full">
                      {views.toLocaleString("es-ES")} reproducciones
                    </span>
                    <span className="absolute w-[52px] h-[52px] rounded-full bg-[#00A0A0]/90 flex items-center justify-center" style={{ animation: "cmp-pulse 1.6s ease-in-out infinite" }}>
                      <Play className="w-5 h-5 text-[#06201c] ml-0.5" fill="currentColor" />
                    </span>
                    <Hook parts={reel.h} className="absolute bottom-3 left-3 right-3 font-stencil uppercase text-[20px] leading-[0.95] text-white" />
                    <div key={runId} className="absolute left-0 bottom-0 h-1 bg-[#00A0A0]" style={{ width: 0, animation: "cmp-fill 3.4s linear forwards" }} />
                  </div>
                  <div data-testid="reel-comments" className="mt-3 flex flex-col gap-2">
                    {comments.map((c, i) => (
                      <div key={`${runId}-${i}`} className="cmp-anim-in flex gap-2 items-start opacity-0">
                        <span className="w-6 h-6 rounded-full bg-[#243430] flex-none" />
                        <div className="text-[12.5px]">
                          <span className="font-bold text-white">{c.who}</span>{" "}
                          <span className="text-[#cdd5d3]">{c.txt}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="self-center text-[#00A0A0] font-black text-2xl flex-none rotate-90 md:rotate-0">→</div>

                {/* DM */}
                <div className="flex-1 min-w-0 flex flex-col bg-white/[0.02] border border-[#1c2c2a] rounded-xl p-3">
                  <div className="flex items-center gap-2.5 pb-2.5 border-b border-[#1c2c2a] flex-none">
                    <div className="w-8 h-8 rounded-full border border-[#00A0A0] flex items-center justify-center flex-none">
                      <User className="w-4 h-4 text-[#00A0A0]" />
                    </div>
                    <div>
                      <div className="text-[13px] font-bold text-white leading-tight">Carlos M.</div>
                      <div className="text-[11px] text-[#69716f]">Mensaje directo · ahora</div>
                    </div>
                  </div>
                  <div ref={threadRef} data-testid="dm-thread" className="relative flex-1 flex flex-col gap-2 pt-3 overflow-hidden min-h-[196px]">
                    {msgs.length === 0 && (
                      <div className="absolute inset-x-1 top-3 bottom-1 flex items-center justify-center text-center text-[#5e6664] font-medium text-[12.5px] leading-relaxed font-body">
                        La conversación empieza cuando el contenido
                        <br />
                        ya ha generado confianza.
                      </div>
                    )}
                    {msgs.map((m, i) => {
                      if (m.type === "typing")
                        return (
                          <div key={`${runId}-t-${i}`} className="cmp-anim-in opacity-0 self-end bg-[#00A0A0]/15 border border-[#00A0A0]/40 px-3.5 py-2.5 rounded-2xl rounded-br-[4px] flex gap-1.5">
                            {[0, 1, 2].map((d) => (
                              <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#00A0A0]" style={{ animation: `cmp-blink 1.1s infinite`, animationDelay: `${d * 0.2}s` }} />
                            ))}
                          </div>
                        );
                      if (m.type === "sys")
                        return (
                          <div key={`${runId}-m-${i}`} className="cmp-anim-in opacity-0 self-center bg-[#FFD700]/10 border border-[#FFD700]/35 text-[#FFD700] font-bold text-[11.5px] px-3 py-1.5 rounded-lg uppercase tracking-wide">
                            {m.txt}
                          </div>
                        );
                      const me = m.type === "me";
                      return (
                        <div
                          key={`${runId}-m-${i}`}
                          className={`cmp-anim-in opacity-0 max-w-[88%] px-3 py-2 text-[13px] leading-snug rounded-2xl ${
                            me
                              ? "self-end bg-[#00A0A0]/15 border border-[#00A0A0]/40 rounded-br-[4px] text-white"
                              : "self-start bg-[#172321] border border-[#25302e] rounded-bl-[4px] text-white"
                          }`}
                        >
                          {m.auto && (
                            <span className="flex items-center gap-1 text-[9.5px] font-black tracking-wide text-[#00A0A0] uppercase mb-1">
                              <Zap className="w-2.5 h-2.5" fill="currentColor" /> Automatización
                            </span>
                          )}
                          {m.txt}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Venta cerrada */}
              <div
                data-testid="sale-overlay"
                className={`absolute inset-0 flex flex-col items-center justify-center gap-3.5 bg-[#060c0b]/[0.82] transition-all duration-400 ${
                  showSale ? "opacity-100 visible" : "opacity-0 invisible"
                }`}
              >
                {showSale && (
                  <>
                    <div className="w-[84px] h-[84px] rounded-full border-[3px] border-[#00A0A0] flex items-center justify-center" style={{ boxShadow: "0 0 30px -4px rgba(0,160,160,.7)", animation: "cmp-pop .5s ease" }}>
                      <Check className="w-10 h-10 text-[#00A0A0]" strokeWidth={3} />
                    </div>
                    <b className="font-stencil uppercase text-2xl text-white tracking-wide">Venta cerrada</b>
                    <span className="text-white/50 font-semibold text-[13px] font-body">El sistema lo gestionó de principio a fin.</span>
                  </>
                )}
              </div>
            </div>
          </Reveal>

          {/* Flujo 4 pasos */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-4">
            {FLOW.map((f, i) => {
              const on = step === i + 1;
              const accent = f.accent;
              const Icon = f.icon;
              return (
                <div
                  key={f.nm}
                  data-testid={`flow-step-${i + 1}`}
                  className={`flex flex-col items-center text-center gap-2 px-2.5 py-4 rounded-xl border transition-all duration-300 ${
                    on ? "opacity-100 -translate-y-0.5" : "opacity-50"
                  }`}
                  style={{
                    borderColor: on ? accent : "#1c2c2a",
                    background: on ? `${accent}0f` : "rgba(255,255,255,.015)",
                  }}
                >
                  <div
                    className="w-[42px] h-[42px] rounded-full border-2 flex items-center justify-center transition-all duration-300"
                    style={{
                      borderColor: on ? accent : "#2a3a38",
                      boxShadow: on ? `0 0 18px -3px ${accent}` : "none",
                    }}
                  >
                    <Icon className="w-5 h-5 transition-colors duration-300" style={{ color: on ? accent : "#8b938f" }} />
                  </div>
                  <div className="font-stencil text-[12.5px] uppercase tracking-wide text-white">{f.nm}</div>
                  <div className="text-[11px] text-white/45 leading-tight font-body">{f.de}</div>
                </div>
              );
            })}
          </div>

          <p className="text-center mt-10 italic font-bold text-sm text-[#8fb8b1] font-body">
            Sistema Fuelkers — <b className="text-[#00A0A0] not-italic font-stencil">Donde otros frenan, tú aceleras.</b>
          </p>
        </div>
      </div>
    </section>
  );
}
